// "What VOLUME is this chapter in, and what does it cost?" — the one module
// that answers it, for the gate, the pricing panel, and the reader alike.
//
// Kept separate from its two neighbours on the same "one module per question"
// split they already follow: `premiumAccess.ts` answers "is this chapter
// paywalled" and `productCatalog.ts` answers "what can an operator put a price
// on". Both now delegate here, because both questions turned out to be about
// volumes once a volume became the sellable unit (see `VolumeConfig`).
//
// EVERY FUNCTION HERE ITERATES VOLUMES, NEVER `config.premium`. That is the
// whole trick: `bookVolumes` synthesizes a single volume around a single-volume
// book's `premium` block, so a caller never branches on "is this title priced
// per volume" — and the five single-volume books keep behaving byte-identically
// to how they did before volumes existed.

import type { BookConfig, MatterPlacement, VolumeConfig } from "./bookConfig";

/**
 * Every volume of a book, in configured order.
 *
 * A single-volume book has no `volumes` list, so one is synthesized: volume 1,
 * spanning `chapters` end to end, carrying `premium` verbatim. The synthesis is
 * what lets every other function in this module — and in `productCatalog.ts`,
 * and in `premiumAccess.ts` — be written once against volumes with no
 * single/multi branch anywhere.
 *
 * A book with an EMPTY `chapters` synthesizes an empty range (`Math.min()` of
 * nothing is `Infinity`), which no chapter number falls inside. That is the
 * correct answer rather than a crash: such a book binds no chapters, so no
 * chapter belongs to its volume, and `isPremiumChapter`'s fail-closed fallback
 * handles anything asked about it.
 */
export function bookVolumes(config: BookConfig): readonly VolumeConfig[] {
  if (config.volumes !== undefined) return config.volumes;
  const chapters = config.chapters ?? [];
  return [
    {
      volumeNumber: 1,
      chapters: { from: Math.min(...chapters), to: Math.max(...chapters) },
      premium: config.premium,
    },
  ];
}

/**
 * The volume binding chapter `n`, or `undefined` when no volume's range reaches
 * it — a chapter configured past the last volume, or a book with no chapters.
 *
 * Ranges are inclusive on both ends and a book's volumes do not overlap, so the
 * first match is the only match; scanning rather than assuming sortedness costs
 * nothing on a list of at most eight and removes an invariant nothing enforces.
 */
export function volumeOfChapter(
  config: BookConfig,
  n: number,
): VolumeConfig | undefined {
  return bookVolumes(config).find(
    (volume) => n >= volume.chapters.from && n <= volume.chapters.to,
  );
}

/**
 * The product id that unlocks chapter `n`, or `undefined` when nothing sells it
 * (a wholly-free volume, a free book, or a chapter outside every volume).
 *
 * THE FIX FOR THE OVER-GRANT LIVES HERE. Every gate call site used to pass the
 * book-wide `config.premium?.productId`, so an entitlement minted from volume
 * two's cover satisfied volume three's chapters too. Resolving per chapter
 * means volume three asks about volume three's id, which that entitlement is
 * not.
 *
 * Answers for a volume's FREE sample chapter as well as its paid ones — the
 * question is "which product owns this chapter", not "is this chapter paid".
 * Callers that care about the latter ask `isPremiumChapter` and short-circuit
 * before the id is ever used.
 */
export function productIdForChapter(
  config: BookConfig,
  n: number,
): string | undefined {
  return volumeOfChapter(config, n)?.premium?.productId;
}

/**
 * The chapter a matter document hangs off, or `undefined` when the book has no
 * chapters for a whole-book placement to reach.
 *
 * `before`/`after` name their chapter outright. `front`/`back` don't, so they
 * resolve to the first/last CONFIGURED chapter — the same choice
 * `src/reader/shelf.ts` makes when it files whole-book matter under the volume
 * owning the first/last chapter. Deriving both from `config.chapters` rather
 * than from the served catalog keeps this pure and keeps the build and the
 * console answering identically for a book mid-seed.
 */
export function anchorChapterOfMatter(
  config: BookConfig,
  placement: MatterPlacement,
): number | undefined {
  const chapters = config.chapters ?? [];
  if (chapters.length === 0) return undefined;
  switch (placement.at) {
    case "before":
    case "after":
      return placement.chapter;
    case "front":
      return Math.min(...chapters);
    case "back":
      return Math.max(...chapters);
  }
}

/**
 * The product that unlocks a matter document, or `undefined` when nothing sells
 * the volume it sits in — a wholly-free volume, a free book, or a placement
 * anchored past every volume.
 *
 * DERIVED FROM PLACEMENT, NEVER STORED. `matterMaster.premium` is a bare
 * boolean on purpose: letting a document name its own product would let an
 * afterword bound to volume three be sold under volume two's id, which is
 * exactly the over-grant `productIdForChapter` was written to end. Asking the
 * placement instead means a document can only ever be sold by the volume it is
 * physically bound into.
 *
 * `undefined` here is what makes the paywall toggle refusable: a document whose
 * volume sells nothing has no way to be unlocked, so the console declines to
 * lock it rather than stranding it behind a product nobody can buy.
 */
export function productIdForMatter(
  config: BookConfig,
  placement: MatterPlacement,
): string | undefined {
  const anchor = anchorChapterOfMatter(config, placement);
  return anchor === undefined ? undefined : productIdForChapter(config, anchor);
}

/**
 * How many of this title's volumes are sold — the "N" in "1 free + N paywalled
 * volumes", and the member count the complete-title bundle is priced from
 * (`defaultSeriesListAmountCents`, `model/pricing.ts`).
 *
 * STATIC, from config alone: it counts every CONFIGURED paywalled volume,
 * seeded or not, because the bundle is sold as a preorder for the complete run
 * rather than for what happens to be readable today. `publishedPaywalledVolumeCount`
 * is the live half of that pair.
 */
export function paywalledVolumeCount(config: BookConfig): number {
  return bookVolumes(config).filter((v) => v.premium !== undefined).length;
}

/**
 * Is every chapter this volume binds actually present in the served catalog?
 *
 * `seededChapters` is whatever the CALLER's layer treats as "published", and
 * the two callers deliberately differ: reader-facing surfaces pass the chapter
 * numbers of the `CatalogBook` they already fetched (what a shopper can
 * genuinely open), the admin panel passes a `retellMaster` scan (the freshest
 * truth, not waiting on a corpus rebuild). Taking the set as an argument is
 * what keeps this pure and keeps both layers from inventing their own rule for
 * the same word.
 *
 * An empty volume range is NOT published — `every` over nothing is vacuously
 * true, which would report a volume binding no chapters as ready to read.
 */
export function volumeIsPublished(
  volume: VolumeConfig,
  seededChapters: ReadonlySet<number>,
): boolean {
  const { from, to } = volume.chapters;
  if (to < from) return false;
  for (let n = from; n <= to; n++) {
    if (!seededChapters.has(n)) return false;
  }
  return true;
}

/**
 * How many paywalled volumes a reader could actually start today — the
 * "available now" against `paywalledVolumeCount`'s "complete run", so a bundle
 * CTA can say "3 of 7 volumes ready now, the rest as they publish" and have it
 * update itself as volumes ship rather than needing the copy re-edited.
 */
export function publishedPaywalledVolumeCount(
  config: BookConfig,
  seededChapters: ReadonlySet<number>,
): number {
  return bookVolumes(config).filter(
    (v) => v.premium !== undefined && volumeIsPublished(v, seededChapters),
  ).length;
}

/**
 * The one sentence every surface says about a complete-title bundle's
 * availability — `null` when the whole run is ready, because then a shopper has
 * nothing to be warned about and the bundle is simply what it says.
 *
 * ONE STRING, THREE SURFACES: the paywall plate, the library shelf, and the
 * pricing panel (which adds its own "All N volumes ready now." for the `null`
 * case, since an operator wants the status either way). The COUNTS each surface
 * feeds in deliberately come from different places — see `volumeIsPublished` —
 * but the wording a shopper reads must not, and it was written out three times
 * before this existed.
 */
export function volumeAvailabilityNote(
  publishedCount: number,
  totalCount: number,
): string | null {
  if (publishedCount >= totalCount) return null;
  return (
    `${String(publishedCount)} of ${String(totalCount)} volumes ready now` +
    " — the rest as they publish."
  );
}
